import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  RolePermissionFormModal,
  RolePermissionViewModal,
} from 'components/modals/modules/admin-module/RolePermissionModal';
import type {
  RolePermissionFormData,
  RolePermissionRecord,
} from 'components/modals/modules/admin-module/RolePermissionModal';
import { formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useRolePermissions, useCreateRolePermission, useUpdateRolePermission, useDeleteRolePermission } from 'hooks/api/useRolePermissions';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const emptyFormData: RolePermissionFormData = {
  roleCode: '',
  roleName: '',
  description: '',
  visibleModules: [],
  moduleActions: {},
};

// ============================================================================
// MAIN SCREEN
// ============================================================================

const RolePermissionScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useRolePermissions();
  const { createRolePermission } = useCreateRolePermission();
  const { updateRolePermission } = useUpdateRolePermission();
  const { deleteRolePermission } = useDeleteRolePermission();

  // Data from API
  const roles = (apiData as unknown as RolePermissionRecord[]) || [];
  

  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [, setSelectedRows] = useState<RolePermissionRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<RolePermissionRecord | null>(null);
  const [formData, setFormData] = useState<RolePermissionFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return roles.filter((role) => {
      const matchesSearch = !searchQuery ||
        role.roleCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        role.roleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (role.description || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === '' ||
        (statusFilter === 'Active' && role.isActive) ||
        (statusFilter === 'Inactive' && !role.isActive);

      const matchesType = typeFilter === '' ||
        (typeFilter === 'system' && role.isSystemRole) ||
        (typeFilter === 'custom' && !role.isSystemRole);

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [roles, searchQuery, statusFilter, typeFilter]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData(emptyFormData);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((record: RolePermissionRecord) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setFormData({
      roleCode: record.roleCode,
      roleName: record.roleName,
      description: record.description || '',
      visibleModules: [...record.visibleModules],
      moduleActions: Object.fromEntries(
        Object.entries(record.moduleActions).map(([k, v]) => [k, [...v]])
      ),
    });
    setFormModalOpen(true);
  }, []);

  const handleView = useCallback((record: RolePermissionRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    const action = formMode === 'create' ? 'Create' : 'Save';
    const message = formMode === 'create'
      ? 'Are you sure you want to create this role permission?'
      : 'Are you sure you want to save changes to this role permission?';

    setConfirmDialog({
      open: true,
      title: `${action} Role Permission`,
      message,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (formMode === 'create') {
              await createRolePermission({
                roleCode: formData.roleCode,
                roleName: formData.roleName,
                description: formData.description || undefined,
                visibleModules: formData.visibleModules,
                moduleActions: formData.moduleActions,
              });
              await refetch();
            } else if (selectedRecord) {
              await updateRolePermission(selectedRecord.id, {
                roleName: formData.roleName,
                description: formData.description || undefined,
                visibleModules: formData.visibleModules,
                moduleActions: formData.moduleActions,
                rowVersion: selectedRecord.rowVersion,
              });
              await refetch();
            }

          showMessage(`Role permission ${formMode === 'create' ? 'created' : 'updated'} successfully`, 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || `Failed to ${formMode === 'create' ? 'create' : 'update'} role permission`, 'error');
        }
      },
    });
  }, [formMode, formData, roles.length, selectedRecord, showMessage, createRolePermission, updateRolePermission, refetch]);

  const handleDelete = useCallback((record: RolePermissionRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Role Permission',
      message: `Are you sure you want to delete role "${record.roleName}" (${record.roleCode})?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteRolePermission(record.id);
            await refetch();

          showMessage('Role permission deleted successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to delete role permission', 'error');
        }
      },
    });
  }, [showMessage, deleteRolePermission, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: RolePermissionRecord): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      {
        id: 'view',
        label: 'View Details',
        icon: <IconSystem.eye size={18} />,
        onClick: () => handleView(row),
      },
      {
        id: 'edit',
        label: 'Edit',
        icon: <IconSystem.edit size={18} />,
        onClick: () => handleEdit(row),
      },
    ];

    if (!row.isSystemRole) {
      actions.push({
        id: 'delete',
        label: 'Delete',
        icon: <IconSystem.trash size={18} />,
        onClick: () => handleDelete(row),
        variant: 'danger' as const,
      });
    }

    return actions;
  }, [handleView, handleEdit, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<RolePermissionRecord>[] = useMemo(() => [
    { id: 'roleCode', header: 'Role Code', accessorKey: 'roleCode', size: 150 },
    { id: 'roleName', header: 'Role Name', accessorKey: 'roleName', size: 160 },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      size: 250,
      cell: ({ getValue }: any) => getValue() || '—',
    },
    {
      id: 'isSystemRole',
      header: 'Type',
      accessorKey: 'isSystemRole',
      size: 100,
      cell: ({ getValue }: any) => {
        const isSystem = getValue();
        return (
          <span style={{
            padding: '0.15rem 0.5rem',
            borderRadius: '4px',
            fontSize: '0.75rem',
            background: isSystem ? 'rgba(99, 102, 241, 0.15)' : 'rgba(156, 163, 175, 0.15)',
            color: isSystem ? '#818cf8' : '#9ca3af',
          }}>
            {isSystem ? 'System' : 'Custom'}
          </span>
        );
      },
    },
    {
      id: 'visibleModules',
      header: 'Modules',
      accessorKey: 'visibleModules',
      size: 90,
      cell: ({ getValue }: any) => {
        const modules = getValue() as string[];
        return <span style={{ fontSize: '0.82rem' }}>{modules?.length || 0}</span>;
      },
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }: any) => {
        const isActive = getValue();
        return (
          <span style={getStatusBadgeStyle(isActive ? 'ACTIVE' : 'INACTIVE')}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Role',
      onClick: handleAddNew,
      tooltip: 'Add New Role Permission',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Role Permissions"
        icon={<IconSystem.lock size={20} />}
        description="Manage role-based permissions and menu access"
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search roles...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {/* Error */}
        {apiError && roles.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load role permissions</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && roles.length === 0 ? (
          <GridLoadingSkeleton columns={5} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                rowActions={getRowActions}
                enableRowSelection
                enablePagination
                enableColumnFilters={false}
                onSelectionChange={setSelectedRows}
                emptyMessage="No role permissions found"
              />
            )}
          </div>

          {/* Filter Panel */}
          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Search status..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Role Type"
                  options={[
                    { value: '', label: 'All Types' },
                    { value: 'system', label: 'System Roles' },
                    { value: 'custom', label: 'Custom Roles' },
                  ]}
                  value={typeFilter}
                  onChange={(val: string | string[] | null) => setTypeFilter((val as string) || '')}
                  placeholder="Search types..."
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setTypeFilter(''); }}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
                Apply
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Form Modal */}
      <RolePermissionFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        isSystemRole={selectedRecord?.isSystemRole}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <RolePermissionViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default RolePermissionScreen;
